import type { GoalProgress } from '@budget/shared';

interface Props {
  progress: GoalProgress;
}

/**
 * Jauge de progression d'un pôle — jamais un camembert : on compare une part à
 * un tout, pas des parts entre elles.
 *
 * La piste est un pas clair de la même rampe que le remplissage, pour que
 * l'état se lise sur toute la barre. Le petit repère marque la trajectoire
 * attendue à date : sans lui, une jauge à moitié pleine ne dit pas si c'est
 * bien ou mal.
 */
export function GoalMeter({ progress }: Props) {
  const ratio = progress.progressRatio ?? 0;
  const expectedRatio =
    progress.expectedCents !== null && progress.targetAmountCents !== null
      ? Math.min(1, Math.max(0, progress.expectedCents / progress.targetAmountCents))
      : null;

  return (
    <div
      className="relative h-2.5 w-full overflow-hidden rounded-full bg-series-track"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(ratio * 100)}
    >
      <div
        className="h-full rounded-full bg-series"
        style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 3 : 0)}%` }}
      />
      {expectedRatio !== null && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 w-0.5 bg-ink"
          style={{ left: `calc(${expectedRatio * 100}% - 1px)`, opacity: 0.45 }}
        />
      )}
    </div>
  );
}

const LABELS: Record<GoalProgress['status'], { icon: string; text: string; tone: string }> = {
  reached: { icon: '✓', text: 'Objectif atteint', tone: 'text-success-text' },
  ahead: { icon: '▲', text: 'En avance', tone: 'text-success-text' },
  'on-track': { icon: '=', text: 'À l’heure', tone: 'text-ink-secondary' },
  behind: { icon: '▼', text: 'En retard', tone: 'text-critical' },
  open: { icon: '•', text: 'Sans échéance', tone: 'text-ink-secondary' },
};

/** Le statut porte une couleur, mais jamais seule : icône et mot obligatoires. */
export function GoalStatus({ status }: { status: GoalProgress['status'] }) {
  const label = LABELS[status];
  return (
    <span className={`text-xs font-medium ${label.tone}`}>
      <span aria-hidden="true">{label.icon}</span> {label.text}
    </span>
  );
}
